import { get, getForClient, HttpResponse } from './request';

import { type Props as PGCardProps } from '../components/PGCard';

/**
 * 详情分页接口（tCharityService_pageGetPlaygroundDetailDto）入参
 *
 * ⚠️  字段规则：
 * - provinceCode / cityCode 传的是「内部行政区 id」（AdminDivisionOptionDto.id），
 *   即供应链 DB 维护的 id，不是 adCode（国标编码）。
 *   来源：tCharityService_listAllProvinceCityOptions 返回的 AdminDivisionOptionDto.id
 * - playgroundName: 操场名称关键词（模糊搜索，后端会转简体匹配，前端传前需 trim()）
 */
export interface PlaygroundDetailListParams {
  /** 语言类型 */
  lanType: string;
  /** 每页数量 */
  pageSize: number;
  /** 页码 */
  pageNo: number;
  /** 建设状态：EXECUTED(已建成) / RESERVE(筹建中) / 空(全部) */
  status?: string;
  /** 省内部行政区 id（⚠️ 取 AdminDivisionOptionDto.id，不是 adCode） */
  provinceCode?: string;
  /** 市内部行政区 id（⚠️ 取 AdminDivisionOptionDto.id，不是 adCode） */
  cityCode?: string;
  /** 操场名称关键词（支持繁体输入，后端归一为简体匹配） */
  playgroundName?: string;
}

export interface SpliceFloorGroundParams {
  lanType: string;
  playgroundType: string;
  count: number;
  status: string;
}

export interface PlaygroundDetailListResContent {
  page: {
    pageSize: number;
    pageNo: number;
    totalCount: number;
  };
  pageContent: PGCardProps[];
}

/**
 * 获取公益操场列表
 * @param params PlaygroundDetailListParams
 * @returns HttpResponse<PlaygroundDetailListResContent>
 */
export const getPlaygroundDetailList = (params: PlaygroundDetailListParams) => {
  return getForClient<PlaygroundDetailListParams, HttpResponse<PlaygroundDetailListResContent>>(
    '/smart/view/charity/r/tCharityService_pageGetPlaygroundDetailDto',
    params,
  ).then((res) => {
    const { code } = res;
    if (code === 0) {
      return res;
    }
    throw res;
  });
};

// 儿童操场页面获取操场数据
export const getPlaygroundListData = (params: PlaygroundDetailListParams) => {
  return get<PlaygroundDetailListParams, HttpResponse<PlaygroundDetailListResContent>>(
    '/smart/view/charity/r/tCharityService_pageGetPlaygroundDetailDto',
    params,
  );
};

// 儿童操场页面获取操场最新  拼接地板操场 （修改接口）
export const getSpliceFloorGroundList = (params: SpliceFloorGroundParams) => {
  return get<SpliceFloorGroundParams, HttpResponse<PlaygroundDetailListResContent>>(
    '/smart/view/charity/r/tCharityService_listPlaygroundDetailDto',
    params,
  );
};

/**
 * 地图统计接口（tCharityService_statisticPlaygroundData）入参
 *
 * ⚠️  字段规则：
 * - provinceAdCode / cityAdCode 传的是「国标行政区编码 adCode」（AdminDivisionOptionDto.adCode），
 *   不是内部 id。
 *   与详情分页接口（pageGetPlaygroundDetailDto）的 provinceCode/cityCode 传 id 的规则相反，
 *   切勿混淆！
 * - lanType 为必填，影响返回的 name 语言（简繁体）及前端缓存 key。
 */
export interface StatisticPlaygroundDataParams {
  /** 地图层级：GLOBAL | COUNTRY | PROVINCE | CITY | COUNTY */
  acType: string;
  /** 语言类型，必填（影响 name 简繁体及缓存 key） */
  lanType?: string;
  /** 省国标编码 adCode（⚠️ 取 AdminDivisionOptionDto.adCode，不是 id） */
  provinceAdCode?: string;
  /** 市国标编码 adCode（⚠️ 取 AdminDivisionOptionDto.adCode，不是 id） */
  cityAdCode?: string;
}
/**
 * 地图统计接口返回的 marker 数据
 * - 省级 marker: name 返回省名
 * - 市级 marker: name 返回市名，附带 provinceName
 * - 区县级 marker: name 返回区县名，附带 provinceName/cityName
 * - 操场 marker: name 返回操场名，附带 serialNo/provinceName/cityName/countyName
 */
export interface StatisticPlaygroundDataResContent {
  /** marker 图标资源 */
  imgSrc: string;
  /** 操场数量 */
  count: number;
  /** 当前 marker 行政区编码 */
  adCode: string;
  /** 父级行政区编码 */
  parentAdCode: string;
  /** 经度 */
  longitude: number;
  /** 纬度 */
  latitude: number;
  /** 操场编号（COUNTY 层级必返回，与搜索候选 pgNo 对齐用于高亮） */
  serialNo: string;
  /** 展示名称（省名/市名/区县名/操场名，跟随 lanType 简繁体） */
  name?: string;
  /** 省名称 */
  provinceName?: string;
  /** 市名称 */
  cityName?: string;
  /** 区县名称 */
  countyName?: string;
}

/**
 * 将「台湾」行政区名称归一为「台湾省」，覆盖简体与繁体两种语言环境。
 *
 * ⚠️  全匹配替换：仅当字段值严格等于下表左侧的值时才替换，不做子串替换，
 *    避免数据本身已是「台湾省 / 台灣省 / 臺灣省」时被替换成「台湾省省 / 台灣省省 / 臺灣省省」。
 *    后端在 lanType=zh-CN 返回「台湾」；lanType=zh-HK（港式正体）可能返回「臺灣」或「台灣」，
 *    两种繁体写法均覆盖。统一在接口请求返回处处理，确保 /public-welfare、
 *    /public-welfare/playground、/public-welfare/playground/details 三个页面所有展示位置一致变为「…省」。
 *
 *    | 原值   | 替换为  |
 *    |--------|---------|
 *    | 台湾   | 台湾省  |
 *    | 台灣   | 台灣省  |
 *    | 臺灣   | 臺灣省  |
 */
const normalizeTaiwanName = (name?: string): string | undefined => {
  switch (name) {
    case '台湾':
      return '台湾省';
    case '台灣':
      return '台灣省';
    case '臺灣':
      return '臺灣省';
    default:
      return name;
  }
};

/**
 * 递归处理省市筛选项（AdminDivisionOptionDto）的 name 字段及其 children 树。
 */
const normalizeProvinceCityOptions = (data?: AdminDivisionOptionDto[]): AdminDivisionOptionDto[] => {
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((item) => ({
    ...item,
    name: normalizeTaiwanName(item.name) as string,
    children: item.children ? normalizeProvinceCityOptions(item.children) : item.children,
  }));
};

/**
 * 处理地图统计 marker（StatisticPlaygroundDataResContent）的 name / provinceName / cityName / countyName 字段。
 */
const normalizeStatisticPlaygroundData = (
  data?: StatisticPlaygroundDataResContent[],
): StatisticPlaygroundDataResContent[] => {
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((item) => ({
    ...item,
    name: normalizeTaiwanName(item.name),
    provinceName: normalizeTaiwanName(item.provinceName),
    cityName: normalizeTaiwanName(item.cityName),
    countyName: normalizeTaiwanName(item.countyName),
  }));
};

/**
 * 获取公益操场的统计信息 For PCMap
 * @param params StatisticPlaygroundDataParams
 * @returns HttpResponse<StatisticPlaygroundDataResContent>
 */
export const getStatisticPlaygroundData = (params: StatisticPlaygroundDataParams, path?: string) => {
  const requestPath = path || '/smart/view/charity/r/tCharityService_statisticPlaygroundData';
  return getForClient<StatisticPlaygroundDataParams, HttpResponse<StatisticPlaygroundDataResContent[]>>(
    requestPath,
    params,
  ).then((res) => ({
    ...res,
    data: normalizeStatisticPlaygroundData(res.data),
  }));
};

interface GetPGDetailBySerialNoParams {
  lanType: string;
  serialNo: string;
}

/**
 * 根据 serialNo 获取详情
 * @param params GetPGDetailBySerialNoParams
 * @returns HttpResponse<PGCardProps['pgCardData']>
 */
export const getPGDetailBySerialNo = (params: GetPGDetailBySerialNoParams, path?: string) => {
  const requestPath = path || '/smart/view/charity/r/tCharityService_getBySerialNo';
  return getForClient<GetPGDetailBySerialNoParams, HttpResponse<PGCardProps['pgCardData']>>(requestPath, params);
};

interface GetPlaygroundDataResContent {
  [key: string]: any;
}
/**
 * 获取操场统计相关详情
 */
export const getPlaygroundData = () => {
  return get<any, HttpResponse<GetPlaygroundDataResContent>>(
    '/smart/view/charity/r/tCharityService_getPlaygroundDataDto',
  );
};

export const getClientPlaygroundData = () => {
  return getForClient<any, HttpResponse<GetPlaygroundDataResContent>>(
    '/smart/view/charity/r/tCharityService_getPlaygroundDataDto',
  );
};

/**
 * 省市筛选项节点（tCharityService_listAllProvinceCityOptions 响应）
 *
 * ⚠️  两个 id 字段用途完全不同，切勿混用：
 * - id       → 内部行政区 id（供应链 DB），传给「详情分页接口」的 provinceCode/cityCode
 * - adCode   → 国标行政区编码，传给「地图统计接口」的 provinceAdCode/cityAdCode
 *              也是「省市筛选项接口」的 provinceCode 参数（查城市列表时传省 adCode）
 */
export interface AdminDivisionOptionDto {
  /** 内部行政区 id（⚠️ 传给 pageGetPlaygroundDetailDto 的 provinceCode/cityCode） */
  id?: string;
  /** 国标行政区编码（⚠️ 传给 statisticPlaygroundData 的 provinceAdCode/cityAdCode
   *  及 listAllProvinceCityOptions 的 provinceCode） */
  adCode: string;
  /** 行政区名称（跟随 lanType 简繁体） */
  name: string;
  /** 已建成操场数量 */
  count?: number;
  /** 下级行政区列表（树形结构时存在） */
  children?: AdminDivisionOptionDto[];
}

/**
 * 省市筛选项接口（tCharityService_listAllProvinceCityOptions）入参
 *
 * ⚠️  provinceCode 传的是「国标行政区编码 adCode」（AdminDivisionOptionDto.adCode），
 *   不传 → 返回全部省份列表；传省 adCode → 返回该省下城市列表。
 *   注意：此处虽然字段名叫 provinceCode，含义是 adCode，与详情分页接口的 provinceCode（传 id）不同！
 */
export interface ListProvinceCityOptionsParams {
  /** 语言类型 */
  lanType: string;
  /** 省国标编码 adCode（⚠️ 取 AdminDivisionOptionDto.adCode，不是 id） */
  provinceCode?: string;
}

/**
 * 详情列表项（用于搜索候选和详情页列表）
 * - pgNo 与地图 COUNTY 层级 marker 的 serialNo 对齐，前端通过 pgNo == serialNo 实现高亮
 */
export interface PlaygroundDetailItem {
  /** 操场类型，如 PLAYGROUND */
  pgType: string;
  /** 建设状态：EXECUTED(已建成) / RESERVE(筹建中) */
  pgStatus: string;
  /** 建设状态名称，如"已建成" */
  pgStatusName: string;
  /** 操场缩略图 */
  pgThumb: string;
  /** 操场大图 */
  pgImg: string;
  /** 建设前图片 */
  pgBulidFImg: string;
  /** 建设后图片 */
  pgBulidEImg: string;
  /** 建设中图片 */
  pgBulidImg: string;
  /** 操场名称 */
  pgName: string;
  /** 操场地址 */
  pgAddress: string;
  /** 操场编号（与地图 COUNTY marker 的 serialNo 同源） */
  pgNo: string;
  /** 操场编号描述，如"全国第9999座" */
  pgNoStr: string;
  /** 建设时间 */
  pgCreateTime: string;
  /** 公益来源 */
  pgCharitySrc: string;
  /** 累计捐赠楼层数 */
  pgFloorNum: number;
  /** 累计里程数 */
  pgMileage: number | null;
  /** 省内部行政区 id */
  provinceCode?: string;
  /** 省国标编码 adCode */
  provinceAdCode?: string;
  /** 市内部行政区 id */
  cityCode?: string;
  /** 市国标编码 adCode */
  cityAdCode?: string;
  /** 经度 */
  longitude?: string;
  /** 纬度 */
  latitude?: string;
}

/**
 * 省市筛选项接口（逐级请求：不传 provinceCode 查省列表，传值查城市列表）
 */
export const listPlaygroundProvinceCityOptions = (params: ListProvinceCityOptionsParams) => {
  return getForClient<ListProvinceCityOptionsParams, HttpResponse<AdminDivisionOptionDto[]>>(
    '/smart/view/charity/r/tCharityService_listAllProvinceCityOptions',
    params,
  ).then((res) => {
    const { code } = res;
    if (code === 0) {
      // 省市筛选项返回处统一将「台湾」归一为「台湾省」
      return { ...res, data: normalizeProvinceCityOptions(res.data) };
    }
    throw res;
  });
};
